import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useDebounce } from 'primereact/hooks';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useCallback, useEffect, useState } from 'react';

import { useSite } from '../../context/site';
import { NAME } from '../../shared';

function ListItemCreate({ open, onClose, type }) {
    const { IsValidItemName, IsValidStateAbbr, CreateListItem } = useSite();

    const [active, setActive] = useState(true);
    const [isDefault, setIsDefault] = useState(false);
    const [name, dbName, setName] = useDebounce('', 420);
    const [stateAbbr, dbStateAbbr, setStateAbbr] = useDebounce('', 420);
    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');
    const [isValidItemName, setIsValidItemName] = useState(true);
    const [isValidAbbr, setIsValidAbbr] = useState(true);

    const reset = () => {
        setActive(true);
        setIsDefault(false);
        setName('');
        setStateAbbr('');
        setProcessing(false);
        setProcessingError('');
        setIsValidItemName(true);
        setIsValidAbbr(true);
    };

    const onCancel = () => {
        reset();
        onClose({ success: false, message: 'Cancelled' });
    };

    const onCreate = () => {
        let data = {
            active,
            listType: type,
            itemName: name,
            itemIsDefault: isDefault,
            itemData: {}
        };

        if (type === NAME.listType.state) {
            data.itemData.abbr = stateAbbr;
        }

        setProcessing(true);

        CreateListItem(data, (res) => {
            setProcessing(false);

            if (res.status !== 200) {
                setProcessingError(res.message);
            } else {
                reset();
                onClose(res);
            }
        });
    };

    const buttonDisabled = useCallback(() => {
        if (!name) {
            return true;
        }

        if (!isValidItemName) {
            return true;
        }

        if (type === NAME.listType.state && stateAbbr.length !== 2) {
            return true;
        }

        if (!isValidAbbr) {
            return true;
        }

        return false;
    }, [name, isValidAbbr, isValidItemName, stateAbbr, type]);

    const footerContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Create"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onCreate}
                autoFocus
            />
        </div>
    );

    // Validate  Name
    useEffect(() => {
        if (dbName && dbName.trim().length > 0) {
            setIsValidItemName(IsValidItemName(type, dbName));
            return;
        }

        if (!dbName || !dbName.trim()) {
            setIsValidItemName(true);
        }
    }, [dbName, type, IsValidItemName]);

    // Validate Abbr
    useEffect(() => {
        if (type === NAME.listType.state && dbStateAbbr && dbStateAbbr.trim()) {
            setIsValidAbbr(IsValidStateAbbr(dbStateAbbr));
        }
    }, [dbStateAbbr, type, IsValidStateAbbr]);

    return (
        <Dialog
            header={`Create New List Item for ${type}`}
            visible={open}
            onHide={onCancel}
            footer={footerContent}
            style={{ width: '50vw' }}
        >
            <ErrorMessage error={processingError} />

            <ProcessingMessage processing={processing} />

            {/* Item Is Default */}
            <div className="field">
                <label htmlFor="cfce-is-default" style={{ float: 'left', marginRight: '1rem' }}>
                    Is Default
                </label>

                <InputSwitch
                    id="cfce-is-default"
                    checked={isDefault}
                    disabled={processing}
                    onChange={(e) => {
                        setIsDefault(e.value);
                    }}
                />
            </div>

            {/* Item Active */}
            <div className="field">
                <label htmlFor="cfce-active" style={{ float: 'left', marginRight: '1rem' }}>
                    Active
                </label>

                <InputSwitch
                    id="cfce-active"
                    checked={active}
                    disabled={processing}
                    onChange={(e) => {
                        setActive(e.value);
                    }}
                />
            </div>

            {/* Item Name */}
            <div className="field">
                <label htmlFor="cfce-item-name">Item Name</label>
                <br />
                <InputText
                    style={{ width: '100%' }}
                    id="cfce-item-name"
                    disabled={processing}
                    placeholder={
                        type !== NAME.listType.state ? 'Enter item name' : 'Enter state name'
                    }
                    keyfilter={/^[a-zA-Z_ ]*$/}
                    maxLength={42}
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                    }}
                />

                {isValidItemName ? null : (
                    <div style={{ paddingTop: '1rem' }}>
                        <Message
                            severity="error"
                            text={`Name (${name}) for type (${type}) is already in use`}
                        />
                    </div>
                )}
            </div>

            {type !== NAME.listType.state ? null : (
                <div className="field">
                    <label htmlFor="cfce-item-name">State Abbreviation</label>
                    <br />
                    <InputText
                        style={{ width: '100%' }}
                        id="cfce-item-state-abbr"
                        minLength={2}
                        maxLength={2}
                        keyfilter="alpha"
                        disabled={processing}
                        placeholder="Enter state abbreviation"
                        value={stateAbbr}
                        onChange={(e) => {
                            setStateAbbr(e.target.value.toUpperCase());
                        }}
                    />

                    {isValidAbbr ? null : (
                        <div style={{ paddingTop: '1rem' }}>
                            <Message
                                severity="error"
                                text={`State Abbr (${stateAbbr})  is already in use`}
                            />
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
}

function ErrorMessage({ error }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return <ProgressSpinner />;
}

export default ListItemCreate;
